import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Activity, Wifi, ListChecks, CheckCircle2, Contact2, CalendarClock, DollarSign, TrendingUp, Wallet, Megaphone } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid } from "recharts";
import api, { num, money } from "@/lib/api";
import { KPICard, Card, PageHeader, EmptyState } from "@/components/admin/ui";
import GlobalClock from "@/components/admin/GlobalClock";
import WorldMap from "@/components/admin/WorldMap";

function AttentionList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/dashboard/attention").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  const color = { critical: "border-rose-500/30 bg-rose-500/5 text-rose-300", warning: "border-amber-500/30 bg-amber-500/5 text-amber-300", info: "border-blue-500/30 bg-blue-500/5 text-blue-300" };
  return (
    <Card className="p-5">
      <h3 className="font-head font-semibold text-white mb-4">⚠ Needs Attention</h3>
      {items.length === 0 ? <p className="text-sm text-slate-500">All systems healthy. No exceptions detected.</p> : (
        <div className="space-y-2">
          {items.map((a, i) => (
            <div key={i} className={`px-4 py-3 rounded-lg border text-sm ${color[a.level] || color.info}`}>{a.message}</div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [trend, setTrend] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/dashboard/trend?days=7").then((r) => setTrend(r.data.items)).catch(() => {});
    api.get("/dashboard/cities").then((r) => setCities(r.data.items)).catch(() => {});
  }, []);

  const kpis = ov ? [
    { label: "Total Workers", value: num(ov.total_workers), icon: Users, accent: "#1D4ED8", testid: "kpi-total-workers" },
    { label: "Active Workers", value: num(ov.active_workers), icon: Activity, accent: "#10B981" },
    { label: "Online Now", value: num(ov.online_now), icon: Wifi, accent: "#06B6D4" },
    { label: "Tasks Today", value: num(ov.tasks_today), icon: ListChecks, accent: "#3B82F6" },
    { label: "Tasks Completed", value: `${num(ov.tasks_completed)} · ${ov.task_completion_pct}%`, icon: CheckCircle2, accent: "#10B981" },
    { label: "Leads Generated", value: num(ov.leads), icon: Contact2, accent: "#1D4ED8" },
    { label: "Meetings", value: num(ov.meetings), icon: CalendarClock, accent: "#8B5CF6" },
    { label: "Sales", value: num(ov.sales), icon: DollarSign, accent: "#10B981" },
    { label: "Revenue", value: money(ov.revenue), icon: TrendingUp, accent: "#1D4ED8" },
    { label: "Pending Payments", value: money(ov.pending_payments), icon: Wallet, accent: "#F59E0B" },
    { label: "Worker Earnings", value: money(ov.worker_earnings), icon: Wallet, accent: "#06B6D4" },
    { label: "Active Campaigns", value: num(ov.active_campaigns), icon: Megaphone, accent: "#3B82F6" },
  ] : [];

  return (
    <div className="space-y-6">
      <PageHeader title="GTForce Global Command Center" subtitle="Executive overview — the complete state of the organization, right now." />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map((k, i) => <KPICard key={i} {...k} />)}
      </div>

      <div>
        <h3 className="font-head font-semibold text-white mb-3">🌎 Global Operations</h3>
        <WorldMap cities={cities} onSelect={(c) => navigate(`/admin/global-operations?city=${c.city_id}`)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-head font-semibold text-white mb-4">Global Performance Trend · 7 days</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1D4ED8" stopOpacity={0.5} /><stop offset="100%" stopColor="#1D4ED8" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={{ background: "#151D2A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="tasks" stroke="#1D4ED8" fill="url(#g1)" strokeWidth={2} />
              <Area type="monotone" dataKey="leads" stroke="#06B6D4" fillOpacity={0} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <h3 className="font-head font-semibold text-white mb-4">Revenue Trend · 7 days</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => money(v)} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "#151D2A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
              <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <AttentionList />
        <Card className="p-5">
          <h3 className="font-head font-semibold text-white mb-4">🕑 Global Clock — Operating Hubs</h3>
          <GlobalClock />
        </Card>
      </div>
    </div>
  );
}
